import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Transition } from "@headlessui/react";
import { closestCenter, DndContext, PointerSensor, useSensor } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";

import useTheme from "hooks/useTheme";
import useNotification from "hooks/useNotification";
import { getAllAccountsByUser, updateAccountListSortByUserID } from "actions/accounts";

import AccountInfo from "views/components/accounts/AccountInfo";
import AccountListItems from "theme/listItems/AccountsListItem";

const Accounts = () => {
	const dispatch = useDispatch();
	const { appLanguage, themeColor } = useTheme();
	const { notify } = useNotification();
	const sensors = [useSensor(PointerSensor)];

	const [activeAccount, setActiveAccount] = useState(null);
	const [accountsList, setAccountsList] = useState(null);
	const [isDataUpdated, setIsDataUpdated] = useState(null);
	const [accountSortby, setAccountSortBy] = useState(null);

	const accounts = useSelector((state) => state.Accounts);

	useEffect(() => {
		dispatch(getAllAccountsByUser(accountSortby));

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accountSortby]);

	useEffect(() => {
		if (accounts) setAccountsList(accounts);
	}, [accounts]);

	useEffect(() => {
		if (accountsList) setActiveAccount(accountsList[0]);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accountsList]);

	useEffect(() => {
		if (isDataUpdated) handleSubmit();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDataUpdated]);

	const handleDragEnd = ({ active, over }) => {
		if (active.id !== over.id) {
			setAccountsList((accountsList) => {
				const oldIndex = accountsList.findIndex((account, index) => index === active.id);
				const newIndex = accountsList.findIndex((account, index) => index === over.id);

				setIsDataUpdated(true);

				return arrayMove(accountsList, oldIndex, newIndex);
			});
		}
	};

	const handleSubmit = () => {
		dispatch(updateAccountListSortByUserID(accountsList, notify));

		setIsDataUpdated(false);
	};

	return (
		<Transition
			as={Fragment}
			appear={true}
			show={true}
			enter="transition-opacity duration-500"
			enterFrom="opacity-0"
			enterTo="opacity-100"
		>
			<div className="w-full flex">
				<div className="flex flex-col w-[50%] items-center justify-start px-5 space-y-2">
					<div className={`flex items-center justify-between w-full space-x-4 pb-1`}>
						<div className="flex items-center space-x-2">
							<h1 className={`w-fit text-xl font-bold ${themeColor?.text}`}>{appLanguage?.manageAccounts}</h1>
							{/* {isDataUpdated && (
									<button
										className={`text-md bg-candy-red text-white px-3 py-1 rounded-lg`}
										onClick={() => handleSubmit()}
									>
										Save
									</button>
								)} */}
						</div>

						{/* <div className="flex items-center space-x-2">
								<select
									className={`w-fit text-xs border rounded-lg px-2 py-1 ${themeColor?.text}`}
									onChange={(e) => setAccountSortBy(e.target.value.split(","))}
								>
									<option value="">Custom Sort</option>
									<option value="name,false">Sort By Name (A-Z)</option>
									<option value="name,true">Sort By Name (Z-A)</option>
								</select>
							</div> */}
					</div>

					{accountsList ? (
						<div className="flex-grow flex-col w-full overflow-y-auto space-y-8">
							<DndContext
								sensors={sensors}
								collisionDetection={(e) => closestCenter(e)}
								onDragEnd={(e) => handleDragEnd(e)}
							>
								<SortableContext
									items={accountsList?.map((account, index) => index)}
									strategy={verticalListSortingStrategy}
									handle={true}
								>
									{accountsList.map((account, index) => (
										<AccountListItems
											account={account}
											index={index}
											activeAccount={activeAccount}
											setActiveAccount={setActiveAccount}
											key={index}
										/>
									))}
								</SortableContext>
							</DndContext>
						</div>
					) : (
						<div className="w-full flex flex-col items-center justify-center space-y-8">
							<p className={`${themeColor?.text}`}>{appLanguage?.noAccountsFound}</p>
						</div>
					)}
				</div>

				<AccountInfo accountInfo={activeAccount} setActiveAccount={setActiveAccount} />
			</div>
		</Transition>
	);
};

export default Accounts;
